import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Layout } from '../../types';
import apiService from '../../services/api';
import AddPlotModal from '../../components/AddPlotModal';
import {
  MapIcon,
  UserIcon,
  ExclamationTriangleIcon,
  ArrowLeftIcon,
  PlusIcon
} from '@heroicons/react/24/outline';

interface PlotStat {
  status_name: string;
  status_color: string;
  count: number;
  total_value: number;
}

interface AssignedUser {
  id: number;
  username: string;
  full_name: string;
  email: string;
  can_update: boolean;
  can_view_reports: boolean;
  assigned_at: string;
  assigned_by_name?: string;
}

const LayoutDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [layout, setLayout] = useState<Layout | null>(null);
  const [plotStats, setPlotStats] = useState<PlotStat[]>([]);
  const [assignedUsers, setAssignedUsers] = useState<AssignedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddPlots, setShowAddPlots] = useState(false);

  useEffect(() => {
    if (!id) return;
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await apiService.getLayoutDetails(parseInt(id));
        setLayout(response.data.layout);
        setPlotStats(response.data.plotStats || []);
        setAssignedUsers(response.data.assignedUsers || []);
      } catch (err: any) {
        setError(err.message || 'Failed to load layout details');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, showAddPlots]);

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-40"></div>
        <div className="card p-6">
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-3"></div>
          <div className="h-4 bg-gray-200 rounded w-1/3 mb-3"></div>
          <div className="h-4 bg-gray-200 rounded w-1/4"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Link to="/layouts" className="btn-outline inline-flex items-center">
          <ArrowLeftIcon className="h-4 w-4 mr-1" /> Back
        </Link>
        <div className="card p-6 text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 mb-4">{error}</p>
          <button onClick={() => window.location.reload()} className="btn-primary">Retry</button>
        </div>
      </div>
    );
  }

  if (!layout) return null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-1">{layout.name}</h1>
          <p className="text-gray-600">{layout.location}</p>
        </div>
        <Link to="/layouts" className="btn-outline inline-flex items-center">
          <ArrowLeftIcon className="h-4 w-4 mr-1" /> Back to Layouts
        </Link>
      </div>

      {/* Layout Info */}
      <div className="card p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h3 className="font-semibold text-gray-700 mb-2">Details</h3>
          <ul className="text-sm text-gray-600 space-y-1">
            <li><span className="font-medium text-gray-800">Address:</span> {layout.address || 'N/A'}</li>
            <li><span className="font-medium text-gray-800">City:</span> {layout.city || 'N/A'}</li>
            <li><span className="font-medium text-gray-800">State:</span> {layout.state || 'N/A'}</li>
            <li><span className="font-medium text-gray-800">Country:</span> {layout.country || 'N/A'}</li>
            <li><span className="font-medium text-gray-800">Postal Code:</span> {layout.postal_code || 'N/A'}</li>
            <li><span className="font-medium text-gray-800">Total Plots:</span> {layout.total_plots}</li>
            {layout.description && <li><span className="font-medium text-gray-800">Description:</span> {layout.description}</li>}
          </ul>
        </div>
        <div>
          <h3 className="font-semibold text-gray-700 mb-2">Created Info</h3>
          <ul className="text-sm text-gray-600 space-y-1">
            <li><span className="font-medium text-gray-800">Created At:</span> {new Date(layout.created_at).toLocaleString()}</li>
            <li><span className="font-medium text-gray-800">Updated At:</span> {new Date(layout.updated_at).toLocaleString()}</li>
            <li><span className="font-medium text-gray-800">Created By:</span> {layout.created_by_name || 'N/A'}</li>
          </ul>
        </div>
      </div>

      {/* Plot Status Stats */}
      <div className="card p-6">
        <h3 className="font-semibold text-gray-700 mb-4">Plot Status Overview</h3>
        {plotStats.length === 0 ? (
          <p className="text-gray-500">No plot statistics available.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {plotStats.map((stat) => (
              <div key={stat.status_name} className="p-4 border rounded-md text-center" style={{ backgroundColor: stat.status_color + '20' }}>
                <p className="font-semibold" style={{ color: stat.status_color }}>{stat.status_name}</p>
                <p className="text-2xl font-bold text-gray-800">{stat.count}</p>
                <p className="text-sm text-gray-600">₹{Number(stat.total_value).toLocaleString()}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Assigned Users */}
      <div className="card p-6">
        <h3 className="font-semibold text-gray-700 mb-4">Assigned Users</h3>
        {assignedUsers.length === 0 ? (
          <p className="text-gray-500">No users assigned to this layout.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">User</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">Email</th>
                  <th className="px-4 py-2 text-center font-medium text-gray-500">Update</th>
                  <th className="px-4 py-2 text-center font-medium text-gray-500">Reports</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">Assigned By</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">Assigned At</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {assignedUsers.map((u) => (
                  <tr key={u.id}>
                    <td className="px-4 py-2 whitespace-nowrap flex items-center space-x-2"><UserIcon className="h-4 w-4" /><span>{u.full_name || u.username}</span></td>
                    <td className="px-4 py-2 whitespace-nowrap">{u.email}</td>
                    <td className="px-4 py-2 text-center">{u.can_update ? '✅' : '❌'}</td>
                    <td className="px-4 py-2 text-center">{u.can_view_reports ? '✅' : '❌'}</td>
                    <td className="px-4 py-2 whitespace-nowrap">{u.assigned_by_name || '-'}</td>
                    <td className="px-4 py-2 whitespace-nowrap">{new Date(u.assigned_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Quick Links */}
      <div className="flex space-x-3">
        <Link to={`/plots?layout_id=${layout.id}`} className="btn-primary inline-flex items-center">
          <MapIcon className="h-5 w-5 mr-1" /> View Plots
        </Link>
        <button
          onClick={() => setShowAddPlots(true)}
          className="btn-outline inline-flex items-center"
        >
          <PlusIcon className="h-5 w-5 mr-1" /> Add Plots
        </button>
      </div>

      {showAddPlots && (
        <AddPlotModal
          layoutId={layout.id}
          onClose={() => setShowAddPlots(false)}
          onCreated={() => {
            // refresh plot stats after creation
            setShowAddPlots(false);
          }}
        />
      )}
    </div>
  );
};

export default LayoutDetailPage; 